// จอที่มัสยิด — นักเรียนเดินผ่านแล้วเห็นชื่อตัวเองขึ้นว่าเช็คแล้ว
//
// ทำไมต้องมีจอ: ถ้าเดินผ่านแล้วไม่มีอะไรตอบกลับ นักเรียนไม่มีทางรู้ว่าติดหรือยัง
// ก็จะเดินผ่านไปเฉยๆ พอมีจอ เขาจะแก้ตัวเองโดยไม่ต้องมีใครสอน — ใครไม่เห็นชื่อ
// จะหยุดแล้วหันหน้าจนกว่าจะขึ้น ซึ่งเป็นสิ่งที่ทำให้ระบบจดจำใบหน้าทำงานได้จริง
//
// **ติดจอไว้เหนือกล้อง** ให้การมองจอเพื่อหาชื่อตัวเองกลายเป็นการมองกล้องไปในตัว
// ถ้าเอาจอไปไว้คนละทางกับกล้อง เด็กจะหันออกจากกล้อง แย่กว่าไม่มีจอ
//
// เป็นรายการไหล ไม่ใช่ทีละคนเต็มจอ — คนผ่านวินาทีละ 1-2 คน ถ้าขึ้นทีละคน
// ชื่อจะวาบหายก่อนเจ้าตัวทันอ่าน ต้องค้างไว้ให้คนที่เดินไปแล้วสามก้าวหันกลับมาเห็น
import React, { useCallback, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import { isSidebarHidden, setSidebarHidden } from '../utils/sidebarToggle';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';
const UPLOAD_BASE = API.replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

// จำนวนแถวบนจอ และอายุของแถว
//
// 45 วินาทีเพราะสองเหตุผลคนละทาง: สั้นพอที่ชื่อกับรูปนักเรียนจะไม่ค้างอยู่บนจอ
// ในที่สาธารณะนานเกินจำเป็น และยาวพอให้คนที่เดินผ่านไปแล้วหันกลับมาดูทัน
// เครื่องหมายสองแบบ ต่างกันที่ "รูปร่าง" ไม่ใช่แค่สี — จอแขวนอยู่หลายเมตร
// สีแยกได้ยากกว่ารูปทรงตอนเหลือบมอง และเด็กบางคนตาบอดสีเขียว-แดง
//   เพิ่งติด      วงกลมทึบติ๊กเดียว สีเขียวสด — ตัวที่ต้องสะดุดตาที่สุด
//   เช็คไปแล้ว    ติ๊กสองอัน สีจาง — สื่อว่า "ใช่ และรับไปก่อนหน้านี้แล้ว"
const MARK = {
  ok:      { icon: 'ri-checkbox-circle-fill', color: '#2ecc71' },
  already: { icon: 'ri-check-double-fill',    color: '#7fa39a' },
};

const MAX_ROWS = 8;
const ROW_TTL_MS = 45000;

// เงียบเกินสองรอบของ heartbeat ถือว่าหลุด — ดูหมายเหตุที่ป้ายแดง
const clock = (v) => (v ? new Date(v).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '');

/**
 * จอยืนยันตัวด้วยโทเคนประจำเครื่อง ไม่ใช่การล็อกอินเป็นคน
 *
 * ทางปกติคือ **ผูกด้วยรหัส 6 หลัก**: เปิด /prayer/display เปล่าๆ เหมือนกัน
 * ทุกเครื่อง จอขึ้นเลขให้ผู้ดูแลพิมพ์ที่หน้าทะเบียน แล้วรับโทเคนมาทาง POST
 * เก็บลง localStorage ของกล่องนั้น — ไม่มีอะไรลับเดินทางใน URL เลย เพราะ
 * query string ถูก nginx เขียนลง access log (ดูหัว migration
 * 20260922_create_prayer_pairing.sql)
 *
 * ยังรับ ?device=masjid-1&key=<โทเคน> ของเดิมอยู่ สำหรับจอที่ติดตั้งไปก่อน
 * หน้าจะเก็บโทเคนแล้ว **ถอด key ออกจากแถบที่อยู่ทันที** เพราะจอแขวนอยู่ในที่
 * สาธารณะ โทเคนไม่ควรค้างให้ใครเดินมาถ่ายรูปไปได้ — แต่ลบสิ่งที่ nginx บันทึก
 * ไปแล้วไม่ได้ ของใหม่ที่ติดตั้งจึงควรใช้วิธีผูกด้วยรหัสแทน
 *
 * ไม่มีโทเคนแต่มีคนล็อกอินอยู่ = ใช้ token ของคนแทน สำหรับครูที่อยากเปิดดูเฉยๆ
 */
const PAIRED_KEY = 'prayer-display-device';

const readPaired = () => {
  try {
    const raw = localStorage.getItem(PAIRED_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return saved?.code && saved?.token ? { code: saved.code, token: saved.token } : null;
  } catch {
    return null;   // โหมดส่วนตัว หรือค่าที่พังจากรุ่นก่อน
  }
};

const savePaired = (code, token) => {
  try {
    localStorage.setItem(PAIRED_KEY, JSON.stringify({ code, token }));
    // เก็บคู่กับคีย์รายเครื่องแบบเดิมด้วย เผื่อกล่องนี้ถูก bookmark ไว้เป็น ?device=
    localStorage.setItem(`prayer-display-key:${code}`, token);
  } catch { /* โหมดส่วนตัว */ }
};

const readDeviceAuth = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = String(params.get('device') || '').trim().toLowerCase();
    if (!code) return readPaired() || { code: null, token: null };

    const storeKey = `prayer-display-key:${code}`;
    const fromUrl = params.get('key');
    if (fromUrl) {
      savePaired(code, fromUrl);
      params.delete('key');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      return { code, token: fromUrl };
    }
    let saved = null;
    try { saved = localStorage.getItem(storeKey); } catch { /* โหมดส่วนตัว */ }
    if (saved) return { code, token: saved };

    // ?device= ที่ยังไม่เคยผูก — ให้ตกไปที่หน้าขอรหัส โดยส่งรหัสนี้ไปเป็นตัวเลือก
    // ตั้งต้นให้ผู้ดูแล ไม่ใช่เชื่อว่าจอเป็นเครื่องนั้นจริง
    const paired = readPaired();
    return paired && paired.code === code ? paired : { code: null, token: null };
  } catch {
    return { code: null, token: null };
  }
};

// ถามซ้ำทุก 3 วินาทีระหว่างรอผู้ดูแลกดผูก — ไม่มีใครยืนรอนานกว่านั้นได้โดยไม่สงสัย
// ว่ากดแล้วไม่ติด และเป็นคำขอที่ถูกสุดในระบบ (ค้นด้วยแฮชที่มี index)
const PAIR_POLL_MS = 3000;

export default function PrayerDisplay() {
  const [rows, setRows] = useState([]);
  const [session, setSession] = useState(null);
  const [prayerLabel, setPrayerLabel] = useState('');
  const [counts, setCounts] = useState({ eligible: 0, scanned: 0 });
  const [connected, setConnected] = useState(false);
  // ช่องทางที่จอนี้ผูกอยู่ มาจากทะเบียนอุปกรณ์ฝั่ง server ไม่ใช่จาก URL
  const [lane, setLane] = useState(null);
  const [denied, setDenied] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [device, setDevice] = useState(readDeviceAuth);
  const asDevice = !!(device.code && device.token);
  // กล่องเปล่าที่ไม่มีทั้งโทเคนประจำเครื่องและคนล็อกอิน = ทีวีที่เพิ่งติดตั้ง
  // ไม่ใช่กรณีผิดพลาด มันคือจุดเริ่มต้นปกติของการติดตั้งจอ
  const pairing = !asDevice && !getToken();
  const [pair, setPair] = useState(null);        // { code, expiresAt }
  const [pairError, setPairError] = useState(null);
  const sidebarWasHidden = useRef(isSidebarHidden());

  // จอเต็ม — ซ่อน sidebar ไว้ตลอดที่อยู่หน้านี้ แล้วคืนค่าเดิมตอนออก
  useEffect(() => {
    const previous = sidebarWasHidden.current;
    setSidebarHidden(true);
    return () => setSidebarHidden(previous);
  }, []);

  // เดินนาฬิกาไว้ให้แถวจางและหมดอายุเองโดยไม่ต้องรอ event ถัดไป
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadSession = useCallback(async () => {
    if (pairing) return;
    try {
      // จอที่ยืนยันด้วยโทเคนใช้เส้นทางของตัวเอง — endpoint ของครูต้องมี JWT
      const data = asDevice
        ? await fetch(`${API}/prayer/display/state`, {
            headers: { 'X-Display-Code': device.code, 'X-Display-Token': device.token },
          }).then((r) => { if (!r.ok) throw new Error('display state ' + r.status); return r.json(); })
        : (await axiosInstance.get(`${API}/prayer/session/current`)).data;
      setSession(data.session);
      setPrayerLabel(data.prayer_label || '');
      if (data.session) setCounts({ eligible: data.eligible, scanned: data.scanned });
      else setCounts({ eligible: 0, scanned: 0 });
    } catch {
      setSession(null);
    }
  }, [asDevice, pairing, device.code, device.token]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // รอบเปลี่ยนเองตามเวลาละหมาด จอนี้ไม่มีใครมากดอะไร — ถามซ้ำทุกนาที
  useEffect(() => {
    const timer = setInterval(loadSession, 60000);
    return () => clearInterval(timer);
  }, [loadSession]);

  useEffect(() => {
    if (pairing) return undefined;
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      auth: asDevice
        ? { displayCode: device.code, displayToken: device.token }
        : { token: getToken() },
    });

    socket.on('connect', () => {
      socket.emit('joinPrayerDisplay', (res) => {
        if (res?.ok) { setConnected(true); setDenied(null); setLane(res.lane || null); }
        else { setConnected(false); setDenied(res?.reason || 'error'); }
      });
    });

    socket.on('prayerScan', (payload) => {
      setRows((prev) => {
        // คนเดิมแตะซ้ำในรอบเดียวกันต้องเลื่อนแถวเดิมขึ้นมา ไม่ใช่ขึ้นสองแถว
        const others = prev.filter((r) => r.st_id !== payload.st_id);
        return [{ ...payload, shownAt: Date.now() }, ...others].slice(0, MAX_ROWS);
      });
      setCounts((c) => (payload.result === 'ok' ? { ...c, scanned: c.scanned + 1 } : c));
      if (payload.prayer_label) setPrayerLabel(payload.prayer_label);
    });

    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    return () => socket.close();
  }, [asDevice, pairing, device.code, device.token]);

  // ---- ขอรหัสจับคู่ แล้วรอผู้ดูแลชี้ว่าจอนี้คือเครื่องไหน ------------------
  //
  // ทั้งขอรหัสและถามสถานะเป็น POST เพราะความลับ (poll_secret) ต้องอยู่ใน body
  // ไม่ใช่ query string — นั่นคือทั้งหมดที่วิธีนี้แก้: ไม่ให้โทเคนไปโผล่ใน
  // access log ของ nginx และไม่ต้องส่ง URL ที่มีโทเคนให้ใครทางแชต
  //
  // รหัสหมดอายุแล้วขอใหม่เองเงียบๆ เพราะจอแขวนอยู่โดยไม่มีคนเฝ้า คนที่เดินมา
  // ตอนไหนก็ตามต้องเจอเลขที่ใช้ได้จริงเสมอ ไม่ใช่เลขที่ตายไปตั้งแต่เมื่อคืน
  useEffect(() => {
    if (!pairing) return undefined;
    let alive = true;
    let secret = null;

    const post = (path, body) => fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => { if (!r.ok) throw new Error(path + ' ' + r.status); return r.json(); });

    const requestCode = async () => {
      try {
        // ?device= ที่ยังไม่เคยผูกส่งไปเป็น "ตัวเลือกตั้งต้น" ให้ผู้ดูแลเท่านั้น
        const hinted = new URLSearchParams(window.location.search).get('device');
        const data = await post('/prayer/display/pair', { device_hint: hinted || undefined });
        if (!alive) return;
        secret = data.poll_secret;
        setPair({ code: data.pair_code, expiresAt: new Date(data.expires_at).getTime() });
        setPairError(null);
      } catch {
        if (!alive) return;
        secret = null;
        setPair(null);
        setPairError('ต่อเซิร์ฟเวอร์ไม่ได้ — กำลังลองใหม่');
      }
    };

    const tick = async () => {
      if (!alive) return;
      if (!secret) { await requestCode(); return; }
      try {
        const data = await post('/prayer/display/pair/poll', { poll_secret: secret });
        if (!alive) return;
        if (data.status === 'approved' && data.token && data.device?.code) {
          savePaired(data.device.code, data.token);
          setPair(null);
          setDevice({ code: data.device.code, token: data.token });
          return;   // pairing กลายเป็น false — effect นี้ถูกถอดเอง
        }
        if (data.status === 'expired') { secret = null; await requestCode(); }
      } catch {
        // เน็ตสะดุดหนึ่งรอบไม่ต้องทำอะไร รอบหน้าถามใหม่ — ห้ามทิ้งรหัสที่ขึ้นจออยู่
      }
    };

    tick();
    const timer = setInterval(tick, PAIR_POLL_MS);
    return () => { alive = false; clearInterval(timer); };
  }, [pairing]);

  const visible = rows.filter((r) => now - r.shownAt < ROW_TTL_MS);

  // ---- จอที่ยังไม่ได้ผูก: ขึ้นเลขให้ผู้ดูแลพิมพ์ -------------------------
  //
  // เลขต้องโตพอที่คนยืนห่างสองเมตรอ่านออกแล้วพิมพ์ลงมือถือตัวเองได้เลย
  // โดยไม่ต้องปีนไปดูใกล้ๆ — ทีวีแขวนสูง และคนติดตั้งมักยืนอยู่ใต้จอ
  if (pairing) {
    const leftMs = pair ? pair.expiresAt - now : 0;
    const leftMin = Math.floor(Math.max(leftMs, 0) / 60000);
    const leftSec = Math.floor((Math.max(leftMs, 0) % 60000) / 1000);
    return (
      <div style={{ ...S.screen, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
        <div style={S.pairLabel}>จอนี้ยังไม่ได้ผูกกับเครื่องไหน</div>
        {pair ? (
          <>
            {/* เว้นวรรคกลางเลข — คนอ่านเลข 6 หลักรวดเดียวผิดบ่อยกว่าอ่านทีละสามตัว */}
            <div style={S.pairCode}>{pair.code.slice(0, 3)} {pair.code.slice(3)}</div>
            <div style={S.pairHow}>
              เข้าระบบด้วยบัญชีผู้ดูแล → เมนู <b>อุปกรณ์งานละหมาด</b> → ปุ่ม <b>ผูกจอด้วยรหัส</b>
              <br />แล้วพิมพ์เลขนี้ พร้อมเลือกว่าจอนี้คือเครื่องไหน
            </div>
            <div style={S.pairTtl}>
              {leftMs > 0
                ? `เลขนี้ใช้ได้อีก ${leftMin}:${String(leftSec).padStart(2, '0')} นาที — หมดแล้วจะขึ้นเลขใหม่เอง`
                : 'กำลังขอเลขใหม่…'}
            </div>
          </>
        ) : (
          <div style={S.pairHow}>{pairError || 'กำลังขอรหัส…'}</div>
        )}
      </div>
    );
  }

  return (
    <div style={S.screen}>
      {/* หัวจอ — บอกว่ารอบไหน รับถึงกี่โมง และเช็คไปแล้วกี่คน */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <span style={S.prayer}>{session ? prayerLabel : 'ยังไม่ถึงเวลาเช็คชื่อ'}</span>
          {session && <span style={S.until}>รับถึง {clock(session.ps_closes_at)}</span>}
          {/* ป้ายช่องทางบนจอเอง — นักเรียนต้องรู้ว่ากำลังยืนดูจอของช่องไหน
              ไม่งั้นคนที่เดินออกช่องซ้ายจะยืนรอชื่อตัวเองที่จอฝั่งขวา
              และครูที่มาตรวจตอนชื่อไม่ขึ้นจะได้เห็นทันทีว่าจอผูกช่องถูกไหม */}
          {lane && <span style={S.lane}>ช่อง {lane}</span>}
        </div>
        <div style={S.headerRight}>
          <span style={S.countValue}>{counts.scanned.toLocaleString('th-TH')}</span>
          <span style={S.countLabel}>เช็คแล้ว</span>
        </div>
      </div>

      {/* ป้ายแดงเต็มความกว้างเมื่อหลุด — ถ้าจอยังโชว์รายการเดิมค้างไว้เงียบๆ
          ทั้งเด็กและครูจะเข้าใจว่าระบบทำงานอยู่ ทั้งที่ไม่มีใครถูกเช็คเลยทั้งรอบ */}
      {!connected && (
        <div style={S.offline}>
          <i className="ri-wifi-off-line" style={{ marginRight: 12 }}></i>
          {denied === 'forbidden'
            ? (asDevice
                ? 'โทเคนของจอนี้ใช้ไม่ได้แล้ว — ออกโทเคนใหม่ที่เมนูผู้ดูแล'
                : 'บัญชีนี้ไม่มีสิทธิ์เปิดจอนี้ — ให้ครูเวรหอหรือฝ่ายปกครองเข้าระบบบนเครื่องนี้')
            : 'ขาดการเชื่อมต่อ — ยังไม่มีใครถูกเช็คชื่อ แจ้งครูเวร'}
        </div>
      )}

      {/* รายการไหล */}
      <div style={S.list}>
        {visible.length === 0 ? (
          <div style={S.empty}>
            <i className="ri-rfid-line" style={{ fontSize: '4em', display: 'block', marginBottom: '.3em' }}></i>
            {session ? 'เดินผ่านกล้องเพื่อเช็คชื่อ' : 'รอเวลาละหมาดรอบถัดไป'}
          </div>
        ) : (
          visible.map((r) => {
            const age = now - r.shownAt;
            // จางลงตามอายุ ให้สายตาจับได้เองว่าแถวไหนเพิ่งขึ้น ไม่ต้องอ่านเวลา
            const opacity = Math.max(0.35, 1 - (age / ROW_TTL_MS) * 0.8);
            return (
              <div key={r.st_id} style={{ ...S.row, opacity }}>
                {r.st_photo ? (
                  <img src={photoUrl(r.st_photo)} alt="" style={S.photo} />
                ) : (
                  <div style={{ ...S.photo, ...S.photoBlank }}><i className="ri-user-line"></i></div>
                )}
                <i
                  className={(MARK[r.result] || MARK.ok).icon}
                  style={{ ...S.check, color: (MARK[r.result] || MARK.ok).color }}
                ></i>
                <div style={S.nameBox}>
                  <div style={S.name}>{r.st_name}</div>
                  {/* คนที่กลับมาโชว์หน้าซ้ำเพราะไม่มั่นใจ ต้องได้คำตอบว่าติดไปแล้วตอนกี่โมง
                      ยังเป็นติ๊กเขียวเหมือนเดิม เขาไม่ได้ทำอะไรผิด แค่ต้องการความมั่นใจ */}
                  {r.result === 'already' && r.checked_at && (
                    <div style={S.already}>เช็คไปแล้วเมื่อ {clock(r.checked_at)}</div>
                  )}
                </div>
                {/* เวลาอยู่กลางแถว คั่นระหว่างชื่อไทยกับชื่ออาหรับ — ชื่อสองฝั่ง
                    กว้างเท่ากัน (flex 1 1 0 ทั้งคู่) เวลาจึงตกกลางจอเสมอ
                    ไม่ว่าชื่อจะสั้นยาวต่างกันแค่ไหน */}
                <div style={S.time}>{clock(r.at)}</div>
                {/* ชื่ออาหรับชิดขวาในแถวเดียวกัน — เด็กจำนวนมากหาชื่อตัวเองจากชื่อนี้
                    ได้เร็วกว่าชื่อไทย และในหอเดียวกันมีชื่อไทยพ้องกันหลายคน
                    ไม่ขึ้นบรรทัดใหม่ใต้ชื่อไทยเพราะแถวจะสูงขึ้นจนเหลือน้อยแถวบนจอ
                    ในฐานมีชื่อนี้ราวครึ่งเดียว คนที่ไม่มีให้ช่องว่างไว้เฉยๆ ไม่ใช่ขีด — */}
                <div style={S.arabic} dir="rtl">{r.st_arabicname || ''}</div>
              </div>
            );
          })
        )}
      </div>

      {/* คนที่ระบบไม่เห็นจะไม่มีอะไรขึ้นบนจอเลย เขาต้องรู้เองว่าต้องทำยังไงต่อ */}
      <div style={S.footer}>ไม่เห็นชื่อตัวเอง? แตะบัตรที่ครูเวร</div>
    </div>
  );
}

// ขนาดทั้งหมดคิดเป็น vh เพราะจอคือทีวีที่แขวนอยู่ คนอ่านจากระยะหลายเมตร
// ไม่ใช้ clamp() ด้วยเหตุผลเดียวกับจอป้อมยาม — เบราว์เซอร์ในทีวีหลายรุ่นเก่าเกินไป
// และไม่ใช้คลาสของธีมเพราะจอนี้ต้องมืดเสมอ ไม่ตามโหมดสว่าง/มืดของผู้ใช้
const S = {
  screen: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: '#0b1110', color: '#e8efeb',
    display: 'flex', flexDirection: 'column',
    fontFamily: 'inherit', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '2vh 3vh', borderBottom: '1px solid #21302c', flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'baseline', gap: '2vh' },
  prayer: { fontSize: '5vh', fontWeight: 700, lineHeight: 1 },
  until: { fontSize: '2.6vh', color: '#8fa79f' },
  headerRight: { textAlign: 'right', lineHeight: 1 },
  lane: {
    fontSize: '2.2vh', padding: '0.4vh 1.4vh', borderRadius: '999px',
    background: 'rgba(255,255,255,0.14)', letterSpacing: '0.04em',
  },
  countValue: { fontSize: '6vh', fontWeight: 700, color: '#2ecc71', display: 'block' },
  countLabel: { fontSize: '2.2vh', color: '#8fa79f' },
  offline: {
    background: '#7a1c24', color: '#fff', textAlign: 'center',
    padding: '1.6vh', fontSize: '3vh', fontWeight: 600, flexShrink: 0,
  },
  list: { flex: 1, padding: '1.5vh 3vh', display: 'flex', flexDirection: 'column', gap: '1.2vh', overflow: 'hidden' },
  row: {
    display: 'flex', alignItems: 'center', gap: '2.5vh',
    background: '#132420', border: '1px solid #1e3a33', borderRadius: '1.2vh',
    padding: '1.2vh 2.5vh', flexShrink: 0,
  },
  photo: { width: '9vh', height: '9vh', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#1e3a33' },
  photoBlank: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4vh', color: '#5f7a72' },
  check: { fontSize: '6vh', flexShrink: 0 },
  nameBox: { flex: '1 1 0', minWidth: 0 },
  name: { fontSize: '5vh', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.15 },
  already: { fontSize: '2.4vh', color: '#8fa79f', marginTop: '.2vh' },
  // อักษรอาหรับต้องมีฟอนต์ของตัวเอง — คลาส .arabic-text ของโมดูลศาสนาอยู่ใต้
  // html.islamic-rtl ซึ่งจอนี้ไม่ได้อยู่ใต้มัน จึงกำหนดตรงนี้
  arabic: {
    fontFamily: "'Amiri', 'Scheherazade New', 'Traditional Arabic', serif",
    fontSize: '4.4vh', color: '#cfe0da', textAlign: 'right',
    flex: '1 1 0', minWidth: 0,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    lineHeight: 1.3,
  },
  time: { fontSize: '2.8vh', color: '#8fa79f', flexShrink: 0, textAlign: 'center' },
  empty: { margin: 'auto', textAlign: 'center', color: '#5f7a72', fontSize: '3.4vh' },
  pairLabel: { fontSize: '3.4vh', color: '#8fa79f', marginBottom: '2vh' },
  pairCode: {
    fontSize: '22vh', fontWeight: 700, lineHeight: 1, letterSpacing: '0.06em',
    color: '#2ecc71', fontVariantNumeric: 'tabular-nums',
  },
  pairHow: { fontSize: '3.2vh', color: '#e8efeb', marginTop: '4vh', lineHeight: 1.6, padding: '0 4vh' },
  pairTtl: { fontSize: '2.6vh', color: '#8fa79f', marginTop: '3vh' },
  footer: {
    borderTop: '1px solid #21302c', padding: '1.8vh',
    textAlign: 'center', fontSize: '3vh', color: '#b9cfc8', flexShrink: 0,
  },
};
